import { SUPER_CANNONS_BACK_BPS, SUPER_CANNONS_LATERAL_BPS } from './abilities';

/**
 * **A geometria das cartas, neutra entre as arenas** (gate V28I.P).
 *
 * ## Porque ela saiu de dentro do duelo
 *
 * Porque passou a ser feita em três sítios. Os dois canhões temporários eram
 * calculados em `pvp/super-cannons.ts` para a sala do duelo e para a cena do
 * duelo — o que já era a decisão certa, porque uma arma desenhada num sítio e a
 * disparar de outro é um jogador a levar tiros de onde não vê nada. O mundo
 * aberto passou a montar os mesmos dois canhões, e uma terceira cópia da mesma
 * aritmética seria a terceira oportunidade de eles divergirem.
 *
 * O que ficou de cada lado é o que é da ARENA: o raio do casco, em
 * milli-unidades de lá. O que vive aqui é a RELAÇÃO, que é o que foi aprovado —
 * "os canhões ficam um pouco para lá da meia-largura da nave" — e ela não
 * pertence a nenhum modo.
 *
 * ## Nada aqui decide arte, e nada aqui sorteia
 *
 * Não há um sprite, uma escala, uma cor nem um `Math.random`. São dois pontos
 * calculados a partir de quatro números, e a mesma entrada dá a mesma saída em
 * qualquer máquina.
 */

/** Um corpo com posição e mira. É tudo o que esta geometria precisa de saber. */
export interface CardAimedBody {
  readonly x: number;
  readonly y: number;
  readonly aimX: number;
  readonly aimY: number;
}

/**
 * O afastamento lateral de um canhão, nas milli-unidades de quem pergunta.
 *
 * Proporcional ao raio do casco, e nunca um número absoluto: um afastamento
 * fixo pareceria colado a uma nave pequena e perdido ao lado de uma grande.
 */
export function cardSuperCannonLateralMu(hullRadiusMu: number): number {
  return Math.round((hullRadiusMu * SUPER_CANNONS_LATERAL_BPS) / 10_000);
}

/**
 * **As duas BOCAS**, na ordem esquerda e direita.
 *
 * Perpendiculares à mira: o vetor da mira roda com o casco, portanto os dois
 * canhões acompanham-no sem que nada aqui saiba o que é um ângulo. O tiro sai
 * da frente do canhão, à mesma distância a que o tiro do casco sai da frente do
 * casco — senão um canhão dispararia de dentro de si mesmo.
 *
 * Vazia quando a mira é o vetor nulo, que não é uma direção.
 */
export function cardSuperCannonMuzzles(
  body: CardAimedBody,
  hullRadiusMu: number,
): readonly { readonly x: number; readonly y: number }[] {
  const length = Math.hypot(body.aimX, body.aimY);
  if (length === 0) {
    return [];
  }
  const forwardX = body.aimX / length;
  const forwardY = body.aimY / length;
  /* A perpendicular, à esquerda da mira. A outra é o seu simétrico. */
  const sideX = -forwardY;
  const sideY = forwardX;
  const lateral = cardSuperCannonLateralMu(hullRadiusMu);
  return [-1, 1].map((sign) => ({
    x: Math.round(body.x + sideX * lateral * sign + forwardX * hullRadiusMu),
    y: Math.round(body.y + sideY * lateral * sign + forwardY * hullRadiusMu),
  }));
}

/**
 * **Os dois CORPOS**, para uma cena os desenhar.
 *
 * Mais atrás do que as bocas, pela mesma razão que a caça os põe atrás da
 * formação: o corpo do canhão fica ao lado do casco e a boca projeta-se à
 * frente dele. Uma cena que desenhasse o corpo na boca poria a arte meio corpo
 * à frente de onde ela devia estar.
 */
export function cardSuperCannonBodies(
  body: CardAimedBody,
  hullRadiusMu: number,
): readonly { readonly x: number; readonly y: number }[] {
  const length = Math.hypot(body.aimX, body.aimY);
  if (length === 0) {
    return [];
  }
  const forwardX = body.aimX / length;
  const forwardY = body.aimY / length;
  const sideX = -forwardY;
  const sideY = forwardX;
  const lateral = cardSuperCannonLateralMu(hullRadiusMu);
  /* Atrás do centro, na mesma proporção que a caça desenha. */
  const back = Math.round((hullRadiusMu * SUPER_CANNONS_BACK_BPS) / 10_000);
  return [-1, 1].map((sign) => ({
    x: Math.round(body.x + sideX * lateral * sign - forwardX * back),
    y: Math.round(body.y + sideY * lateral * sign - forwardY * back),
  }));
}
